import { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'urql';
import {
  Period,
  MetaAdCampaignObjectiveInput,
  SocialAdsAnalyticsTabular_QueryFragment,
} from '@frontend/gql/social/graphql';
import { getTopSocialAccountsSumInsights } from '../graphql';
import type { SocialAccountData } from '../components/social-accounts-table/social-accounts-table-types';
import { sortSocialAccountsData, AdCampaign } from './use-social-ads-data-utils';

export interface UseSocialAdsSocialAccountsDataProps {
  clientId?: string;
  currentIntervalRange: {
    startDate: string;
    endDate: string;
    precision: Period;
  };
  previousIntervalRange: {
    startDate: string;
    endDate: string;
  };
  startDate: string;
  endDate: string;
  fromOldData: boolean;
  adAccountIds?: string[];
  adCampaignIds?: string[]; // Separate field for campaigns
  adSetIds?: string[]; // Separate field for ad sets
  adCampaignObjectives?: MetaAdCampaignObjectiveInput[];
  memberIds?: string[];
  compareWithPartnershipAds?: boolean;
  tab: string;
  adCampaigns?: AdCampaign[]; // Add adCampaigns for splitting IDs
  isInitialized?: boolean; // Add initialization guard
}

export interface UseSocialAdsSocialAccountsDataResult {
  fetching: boolean;
  refetching: boolean;
  processedData: SocialAccountData[]; // Always an array, even if empty
  handleRefetch: () => void;
}

export const useSocialAdsSocialAccountsData = ({
  clientId,
  currentIntervalRange,
  // previousIntervalRange,
  startDate: _startDate,
  endDate: _endDate,
  // fromOldData,
  adAccountIds,
  adCampaignIds,
  adSetIds,
  adCampaignObjectives,
  memberIds,
  compareWithPartnershipAds: _compareWithPartnershipAds,
  tab,
  isInitialized = true, // Default to true for backward compatibility
}: UseSocialAdsSocialAccountsDataProps): UseSocialAdsSocialAccountsDataResult => {
  const [refetching, setRefetching] = useState(false);

  // Use separate adCampaignIds and adSetIds
  const finalAdCampaignIds = useMemo(() => {
    return adCampaignIds && adCampaignIds.length > 0 ? adCampaignIds : undefined;
  }, [adCampaignIds]);

  const finalAdSetIds = useMemo(() => {
    return adSetIds && adSetIds.length > 0 ? adSetIds : undefined;
  }, [adSetIds]);

  // Execute the GraphQL query
  const [result, executeQuery] = useQuery({
    query: getTopSocialAccountsSumInsights,
    variables: {
      clientId: clientId || '',
      currentIntervalRange: {
        startDate: currentIntervalRange.startDate,
        endDate: currentIntervalRange.endDate,
        precision: 'ALL' as Period, // Force precision to ALL for tabular data
      },
      adAccountIds: adAccountIds && adAccountIds.length > 0 ? adAccountIds : undefined,
      ...(finalAdCampaignIds && { adCampaignIds: finalAdCampaignIds }),
      ...(finalAdSetIds && { adSetIds: finalAdSetIds }),
      ...(adCampaignObjectives &&
        adCampaignObjectives.length > 0 && {
          adCampaignObjectives,
        }),
      memberIds: memberIds && memberIds.length > 0 ? memberIds : undefined,
    },
    pause: !clientId || tab !== 'social-accounts' || !isInitialized,
  });

  const { data, fetching } = result;

  // Use data?.client directly instead of useFragment
  const client = (data as SocialAdsAnalyticsTabular_QueryFragment)?.client;

  // Use shared sorting function to ensure consistency with useAdsQueryData
  const topSocialAccounts: SocialAccountData[] = useMemo(() => {
    const socialAccountsData = client?.sumInsightsBySocialAccounts || [];
    const sortedData = sortSocialAccountsData(socialAccountsData);
    return sortedData; // Return full dataset for client-side pagination
  }, [client]);

  const handleRefetch = useCallback(async () => {
    setRefetching(true);
    try {
      await executeQuery({ requestPolicy: 'network-only' });
    } finally {
      setRefetching(false);
    }
  }, [executeQuery]);

  return {
    fetching,
    refetching,
    processedData: topSocialAccounts,
    handleRefetch,
  };
};
